/**
 * SuperAdminOverrideBanner (Iter 111.1)
 *
 * Persistent, app-wide banner surfacing an ACTIVE Super Admin Override
 * session so every operator sees the elevated posture.
 *
 * Only the Primary Super Admin can activate a session, so this banner
 * only ever appears for that identity — but we always poll the endpoint
 * safely (short-circuits to null for non-PSA) so nothing breaks if the
 * shape changes.
 *
 * Visual: distinct amber-fuchsia colouring (not rose — rose is reserved
 * for Migration Mode). Shows scope chips, activated-by, reason, live
 * countdown, and a "End Now" button routed to /admin/super-admin-override.
 *
 * Polls every 15s so:
 *   • fresh sessions surface within one interval on any tab,
 *   • auto-expired sessions clear the banner without a page reload.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldWarning, ClockCountdown, Lock } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

function fmtSeconds(secs) {
    if (secs == null || secs <= 0) return "0s";
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
}

export default function SuperAdminOverrideBanner() {
    const { user } = useAuth();
    const [session, setSession] = useState(null);
    const [tick, setTick] = useState(0);
    const [ending, setEnding] = useState(false);
    const mounted = useRef(true);

    const fetchStatus = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/override/active");
            if (mounted.current) {
                setSession(data.session || null);
                setTick(0);
            }
        } catch {
            // Silent — endpoint reachable to any authenticated user; failures
            // simply leave the banner hidden.
            if (mounted.current) setSession(null);
        }
    }, []);

    useEffect(() => {
        mounted.current = true;
        fetchStatus();
        const id = setInterval(fetchStatus, 15000);
        return () => { mounted.current = false; clearInterval(id); };
    }, [fetchStatus]);

    // Local per-second countdown ticker without hitting the API.
    useEffect(() => {
        if (!session) return;
        const id = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, [session]);

    const secondsRemaining = useMemo(() => {
        if (!session) return 0;
        return Math.max(0, (session.seconds_remaining || 0) - tick);
    }, [session, tick]);

    // Auto-refresh when countdown hits zero so the banner clears.
    useEffect(() => {
        if (session && secondsRemaining === 0 && tick > 0) fetchStatus();
    }, [session, secondsRemaining, tick, fetchStatus]);

    const endSession = async () => {
        if (!window.confirm("End the active Super Admin Override session now? All future backdated writes will be blocked again.")) return;
        setEnding(true);
        try {
            await api.post("/admin/override/end", { reason: "ended_via_global_banner" });
            toast.success("Override session ended.");
            await fetchStatus();
        } catch (err) {
            toast.error(formatApiError(err, "Could not end session"));
        } finally {
            setEnding(false);
        }
    };

    if (!session) return null;

    const isPrimary = user?.is_primary_admin === true;

    return (
        <div
            className="print:hidden bg-gradient-to-r from-fuchsia-800 via-fuchsia-700 to-amber-700 text-white border-b border-fuchsia-900 shadow-md"
            data-testid="super-admin-override-banner"
        >
            <div className="max-w-screen-2xl mx-auto px-4 py-2 flex flex-wrap items-center gap-3 text-xs">
                <ShieldWarning size={18} weight="fill" className="text-amber-200 shrink-0" />
                <span className="font-bold uppercase tracking-wider text-[10px] bg-fuchsia-950/40 px-1.5 py-0.5 rounded border border-fuchsia-300/40" data-testid="override-banner-title">
                    Super Admin Override · ACTIVE
                </span>

                <span className="opacity-95" data-testid="override-banner-actor">
                    by <b>{session.actor_email}</b>
                </span>

                <span className="flex items-center gap-1 flex-wrap" data-testid="override-banner-scopes">
                    {(session.scopes || []).map((s) => (
                        <span
                            key={s}
                            className="text-[10px] font-mono bg-fuchsia-950/40 border border-fuchsia-300/40 px-1.5 py-0.5 rounded"
                            data-testid={`override-banner-scope-${s}`}
                        >
                            {s}
                        </span>
                    ))}
                </span>

                {session.reason && (
                    <span
                        className="italic opacity-90 max-w-md truncate hidden lg:inline"
                        title={session.reason}
                        data-testid="override-banner-reason"
                    >
                        “{session.reason}”
                    </span>
                )}

                <span className="grow" />

                <span
                    className="inline-flex items-center gap-1 font-mono bg-fuchsia-950/40 border border-fuchsia-300/40 px-2 py-0.5 rounded"
                    data-testid="override-banner-remaining"
                >
                    <ClockCountdown size={12} weight="fill" />
                    {fmtSeconds(secondsRemaining)} remaining
                </span>

                <Link
                    to="/admin/super-admin-override"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-fuchsia-950/40 border border-fuchsia-300/40 hover:bg-fuchsia-950/60 transition-colors text-[11px] font-medium"
                    data-testid="override-banner-manage-link"
                >
                    Manage
                </Link>

                {isPrimary && (
                    <button
                        type="button"
                        onClick={endSession}
                        disabled={ending}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-rose-600 hover:bg-rose-700 border border-rose-900 disabled:opacity-60 disabled:cursor-not-allowed transition-colors text-[11px] font-semibold"
                        data-testid="override-banner-end-btn"
                    >
                        <Lock size={12} weight="fill" />
                        {ending ? "Ending…" : "End Now"}
                    </button>
                )}
            </div>
        </div>
    );
}
